export const INBOX_TYPES = {
  WEB: 'Channel::WebWidget',
  FB: 'Channel::FacebookPage',
  TWITTER: 'Channel::TwitterProfile',
  TWILIO: 'Channel::TwilioSms',
  WHATSAPP: 'Channel::Whatsapp',
  EVOLUTION_API: 'Channel::EvolutionApi',
  API: 'Channel::Api',
  EMAIL: 'Channel::Email',
  TELEGRAM: 'Channel::Telegram',
  LINE: 'Channel::Line',
  SMS: 'Channel::Sms',
  INTERTEL_UC: 'Channel::IntertelUc',
};

/**
 * Características disponibles para los buzones.
 */
export const INBOX_FEATURES = {
  REPLY_TO: 'replyTo', // Responder a mensajes entrantes
  REPLY_TO_OUTGOING: 'replyToOutgoing', // Responder a mensajes salientes
};

/**
 * Mapeo de características del buzón.
 * Se utiliza para verificar si una característica está disponible para un buzón específico.
 */
export const INBOX_FEATURE_MAP = {
  [INBOX_FEATURES.REPLY_TO]: [
    INBOX_TYPES.FB,
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.EVOLUTION_API,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.API,
  ],
  [INBOX_FEATURES.REPLY_TO_OUTGOING]: [
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.EVOLUTION_API,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.API,
  ],
};

export default {
  computed: {
    channelType() {
      return this.inbox.channel_type;
    },
    whatsAppAPIProvider() {
      return this.inbox.provider || '';
    },
    isAPIInbox() {
      return this.channelType === INBOX_TYPES.API;
    },
    isATwitterInbox() {
      return this.channelType === INBOX_TYPES.TWITTER;
    },
    isAFacebookInbox() {
      return this.channelType === INBOX_TYPES.FB;
    },
    isAWebWidgetInbox() {
      return this.channelType === INBOX_TYPES.WEB;
    },
    isATwilioChannel() {
      return this.channelType === INBOX_TYPES.TWILIO;
    },
    isALineChannel() {
      return this.channelType === INBOX_TYPES.LINE;
    },
    isAnEmailChannel() {
      return this.channelType === INBOX_TYPES.EMAIL;
    },
    isATelegramChannel() {
      return this.channelType === INBOX_TYPES.TELEGRAM;
    },
    isATwilioSMSChannel() {
      const { medium: medium = '' } = this.inbox;
      return this.isATwilioChannel && medium === 'sms';
    },
    isASmsInbox() {
      return this.channelType === INBOX_TYPES.SMS || this.isATwilioSMSChannel;
    },
    isATwilioWhatsAppChannel() {
      const { medium: medium = '' } = this.inbox;
      return this.isATwilioChannel && medium === 'whatsapp';
    },
    isAWhatsAppCloudChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP &&
        this.whatsAppAPIProvider === 'whatsapp_cloud'
      );
    },
    is360DialogWhatsAppChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP &&
        this.whatsAppAPIProvider === 'default'
      );
    },
    chatAdditionalAttributes() {
      const { additional_attributes: additionalAttributes } = this.chat || {};
      return additionalAttributes || {};
    },
    isTwitterInboxTweet() {
      return this.chatAdditionalAttributes.type === 'tweet';
    },
    twilioBadge() {
      return `${this.isATwilioSMSChannel ? 'sms' : 'whatsapp'}`;
    },
    twitterBadge() {
      return `${this.isTwitterInboxTweet ? 'twitter-tweet' : 'twitter-dm'}`;
    },
    facebookBadge() {
      return this.chatAdditionalAttributes.type || 'facebook';
    },
    inboxBadge() {
      let badgeKey = '';
      if (this.isATwitterInbox) {
        badgeKey = this.twitterBadge;
      } else if (this.isAFacebookInbox) {
        badgeKey = this.facebookBadge;
      } else if (this.isATwilioChannel) {
        badgeKey = this.twilioBadge;
      } else if (this.isAWhatsAppChannel) {
        badgeKey = 'whatsapp';
      }
      return badgeKey || this.channelType;
    },
    isAWhatsAppChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP ||
        this.isATwilioWhatsAppChannel
      );
    },
    /**
     * Retorna si es un canal de WhatsApp con el proveedor Evolutioin API
     */
    isAnEvolutionApiWhatsAppChannel() {
      return this.channelType === INBOX_TYPES.EVOLUTION_API;
    },
    /**
     * Retorna si el canal de WhatsApp con Evolution API está vinculado y permite enviar mensajes.
     */
    isCanReplyEvolutionApiChannel() {
      return (
        this.isAnEvolutionApiWhatsAppChannel &&
        this.inbox.linked &&
        this.inbox.allow_send_messages
      );
    },
    /**
     * Retorna si el canal es tipo Intertel UC
     */
    isIntertelUCInbox() {
      return this.channelType === INBOX_TYPES.INTERTEL_UC;
    },
  },
  methods: {
    /**
     * Verifica si el buzón tiene una característica específica.
     * @param {string} feature - La característica a verificar.
     * @returns {boolean} - Verdadero si el buzón tiene la característica, falso en caso contrario.
     */
    inboxHasFeature(feature) {
      return INBOX_FEATURE_MAP[feature]?.includes(this.channelType) ?? false;
    },
    /**
     * Obtiene y retorna el nombre a mostrar para un evento del webhook_call
     * @param {*} event - evento del webhook_call
     * @returns Retorna el nombre a mostrar para un evento del webhook_call
     */
    getEventLabel(event) {
      const eventName = event.toUpperCase();
      return this.$t(
        `INBOX_MGMT.ADD.INTERTEL_UC_CHANNEL.WEBHOOK_CALL.EVENTS.${eventName}`
      );
    },
    /// Normaliza un número de teléfono eliminando el prefijo +521
    /// y agregando el prefijo +52.
    /// @param {string} phoneNumber - Número de teléfono a normalizar.
    /// @returns {string} Número de teléfono normalizado.
    normalizedPhoneNumber(phoneNumber) {
      return phoneNumber.replace(/^\+521(\d{10})$/, '+52$1');
    },
  },
};
